import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Gamepad2, Globe, Music, Users, Sparkles, Mountain, TreePine, ChevronRight } from 'lucide-react';
import { VideoModal, VideoCard } from '@/components/VideoModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VRChatMetaQuestPage() {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/videos?page=vr-chat-meta-quest`)
      .then(res => setVideos(res.data))
      .catch(console.error);
  }, []);

  const features = [
    { icon: Globe, title: "Olmec Environments", description: "Explore recreated Olmec civilizations" },
    { icon: Mountain, title: "Mayan Temples", description: "Climb ancient Mayan pyramids" },
    { icon: Sparkles, title: "Kemet Mysteries", description: "Discover Ancient Egyptian wonders" },
    { icon: Music, title: "VR Concerts", description: "Attend live virtual concerts" },
    { icon: Users, title: "Networking Hubs", description: "Connect with global community" },
    { icon: TreePine, title: "Relaxation Zones", description: "Jungle soundscapes & sound baths" },
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="vr-chat-meta-quest-page">
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 animated-gradient opacity-20" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary mb-6">
              <Gamepad2 className="w-4 h-4" />
              <span className="text-sm font-medium">Meta Quest Experience</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-testid="vr-chat-title">
              VR CHAT META QUEST WORLD
            </h1>
            <p className="text-xl text-muted-foreground">
              An immersive, magical adventure built for international networking, global conversation, and experiential learning.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Video */}
      {videos.length > 0 && (
        <section className="py-8">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <VideoCard
                video={videos[0]}
                onClick={setSelectedVideo}
                index={0}
              />
            </motion.div>
          </div>
        </section>
      )}

      {/* Description Section */}
      <section className="py-16 bg-card/50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="p-8 rounded-2xl glass-light">
              <p className="text-lg text-muted-foreground leading-relaxed" data-testid="vr-chat-description">
                The Matrix University VR Chat World is an immersive, magical adventure built for international networking, global conversation, and experiential learning. Visitors explore richly recreated Olmec, Mayan, and Kemet (Ancient Egyptian) environments and mysteries, attend live VR concerts and events, and join interactive classrooms and workshops.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mt-4">
                Adventure features like zip-lining and rock-climbing sit alongside calming jungle soundscapes and digital sound baths for relaxation and guided healing. Social hubs and an otherworldly alien spaceship spark curiosity and cross-cultural dialogue — perfect for K–12 educators, higher-education programs, nonprofits, and organizations supporting neurodiverse learners.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-serif font-bold text-white text-center mb-12"
          >
            World Features
          </motion.h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="p-6 rounded-xl glass-light text-center card-hover"
                data-testid={`world-feature-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* More Videos */}
      {videos.length > 1 && (
        <section className="py-16 bg-card/50">
          <div className="container-custom">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-serif font-bold text-white text-center mb-12"
            >
              VR Chat Videos
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.slice(1).map((video, index) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  onClick={setSelectedVideo}
                  index={index}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
              Ready to Enter the VR Chat World?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join our growing community of learners, educators, and explorers in the metaverse.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
              Contact Us to Get Started
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <VideoModal
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        videoId={selectedVideo?.youtube_id}
        videoUrl={selectedVideo?.youtube_url}
        title={selectedVideo?.title}
      />
    </div>
  );
}
